import { FormArray, FormControl, FormGroup } from '@angular/forms';

// ============================================================================
// ENUMS - Must be defined first as they're used in interfaces below
// ============================================================================

export enum PartnerImportStatus {
  Synced = 'Synced',
  Orphaned = 'Orphaned',
  HasConflicts = 'HasConflicts',
}

export enum PartnerUpdateStatus {
  Pristine = 'Pristine',
  Touched = 'Touched',
}

export enum IntegrationType {
  None = 'None',
  Helsi = 'Helsi',
  Eleks = 'Eleks',
  Lilo = 'Lilo',
  Dila = 'Dila',
  Pharmacy911 = 'Pharmacy911',
  PharmacyAdd = 'PharmacyAdd',
  PharmacyDobrobut = 'PharmacyDobrobut',
}

export enum PartnerType {
  Clinic = 'Clinic',
  Laboratory = 'Laboratory',
  Pharmacy = 'Pharmacy',
}

export enum PartnerStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum PartnerAvailability {
  Available = 'Available',
  Unavailable = 'Unavailable',
}

export enum LocationStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum LocationAvailability {
  Available = 'Available',
  Unavailable = 'Unavailable',
}

export enum ServiceType {
  Generic = 'Generic',
  Pharmacy = 'Pharmacy',
  Laboratory = 'Laboratory',
  Appointment = 'Appointment',
  Stomatology = 'Stomatology',
}

export enum SortDirection {
  Asc = 'Asc',
  Desc = 'Desc',
}

export enum Gender {
  Male = 'Male',
  Female = 'Female',
}

export enum Currency {
  UAH = 'UAH',
  USD = 'USD',
}

export enum AppointmentCategory {
  Consultation = 'Consultation',
  Diagnostics = 'Diagnostics',
  Procedure = 'Procedure',
}

// ============================================================================
// SUPPORTING INTERFACES - Shared types used across entities
// ============================================================================

export interface Contact {
  name: string;
  type: string;
  contactDetails: Partial<ContactDetails>;
  location: Partial<PhysicalAddress>;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface PhysicalAddress {
  city: string;
  district?: string;
  address: string;
  coordinates?: Partial<Coordinates>;
}

export interface ContactDetails {
  phoneNumber: string;
  email: string;
}

export interface DepartmentSchedule {
  dayOfWeek: string;
  from: string;
  to: string;
  formattedHours: string;
}

export interface ScheduleUiModel {
  daysOfWeek: string[];
  from: string;
  to: string;
  formattedHours: string;
}

export interface ScheduleDisplayModel {
  label: string;
  time: string;
}

export interface DynamicContentSection {
  title: string;
  type: string;
  elements: string[];
}

export interface OfferingInstructions {
  items: OfferingInstructionItem[];
}

export interface OfferingInstructionItem {
  id: string;
  type: string;
  description: string;
  value: number;
}

// ============================================================================
// RESPONSE INTERFACES - API response types
// ============================================================================

export interface BasePartnerEntityResponse {
  id: string;
  active: boolean;
  available: boolean;
  importStatus: PartnerImportStatus;
  updateStatus: PartnerUpdateStatus;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  deletedAt: string;
  deletedBy: string;
}

export interface PartnerResponse extends BasePartnerEntityResponse {
  name: string;
  logoUrl?: string;
  integrationEnabled: boolean;
  integration: IntegrationType;
  supportedServices?: ServiceType[];
  contacts: Contact[];
}

export interface LocationResponse extends BasePartnerEntityResponse {
  partnerId: string;
  partnerName: string;
  name: string;
  logoUrl: string;
  integrationEnabled: boolean;
  integration: IntegrationType;
  supportedServices?: ServiceType[];
  contacts: Contact[];
  location: PhysicalAddress;
  schedule: DepartmentSchedule[];
}

export interface DoctorResponse extends BasePartnerEntityResponse {
  partnerId: string;
  partnerExternalId: string;
  fullName: string;
  email: string;
  phone: string;
  specialization: string;
  specializations: string[];
  photoUrl: string;
  originalPhotoHash: string;
  presentationVideoUrl: string;
  dynamicContentSections: DynamicContentSection[];
  experienceInYears: number;
  isEnglishSpeaking: boolean;
  isPersonalDoctor: boolean;
  isPopular: boolean;
}

export interface OfferingResponse extends BasePartnerEntityResponse {
  $type: number;
  externalId: string;
  externalCode: string;
  partnerId: string;
  serviceType: ServiceType;
  name: string;
  description: string;
  code: string;
  alternativeCodes: string[];
  price: number;
  currency: string;
  instructionsRaw: string;
  instructions: OfferingInstructions;
  integration: IntegrationType;
  canBeOrderedOnline: boolean;
  canProduceMedicalData: boolean;
  allowedForGenders: Gender[];
  minAge: number;
  maxAge: number;
  appointmentCategory: AppointmentCategory | null;
}

// ============================================================================
// V2 INTERFACES - Domain model types
// ============================================================================

export interface PartnerV2 {
  id: string;
  name: string;
  logoUrl?: string;
  active: boolean;
  available: boolean;
  integrationEnabled: boolean;
  integration: IntegrationType;
  supportedServices: ServiceType[];
  importStatus: PartnerImportStatus;
  updateStatus: PartnerUpdateStatus;
  contacts: Contact[];
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
}

export interface LocationV2 {
  id: string;
  partnerId: string;
  partnerName: string;
  name: string;
  logoUrl?: string;
  active: boolean;
  available: boolean;
  integrationEnabled: boolean;
  integration: IntegrationType;
  supportedServices: ServiceType[];
  importStatus: PartnerImportStatus;
  updateStatus: PartnerUpdateStatus;
  contacts: Contact[];
  location: PhysicalAddress;
  schedule: DepartmentSchedule[];
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: string;
}

export interface DoctorV2 {
  id: string;
  fullName: string;
  integrationEnabled: boolean;
  importStatus: PartnerImportStatus;
  integration: IntegrationType;
  partnerId: string;
  active: boolean;
  available: boolean;
  updateStatus: PartnerUpdateStatus;
  photoUrl: string;
  specialization: string;
  specializations: string[];
  presentationVideoUrl: string;
  isPersonalDoctor: boolean;
  isPopular: boolean;
}

export interface OfferingV2 {
  $type: number;
  id: string;
  serviceType: ServiceType;
  name: string;
  description: string;
  code: string;
  alternativeCodes: string[];
  externalId: string;
  externalCode: string;
  price: number;
  currency: string;
  integrationEnabled: boolean;
  importStatus: PartnerImportStatus;
  integration: IntegrationType;
  active: boolean;
  available: boolean;
  partnerId: string;
  updateStatus: PartnerUpdateStatus;
  allowedForGenders: Gender[];
  minAge: number;
  maxAge: number;
  canBeOrderedOnline: boolean;
  canProduceMedicalData: boolean;
  appointmentCategory: AppointmentCategory | null;
}

// ============================================================================
// PAYLOAD INTERFACES - Request body types
// ============================================================================

export interface PartnerPayloadV2 {
  id?: string;
  integrationEnabled?: boolean;
  integration?: IntegrationType;
  supportedServices?: ServiceType[];
  fileName?: string;
  name: string;
  active: boolean;
  available: boolean;
  contacts: Partial<Contact>[];
}

export interface LocationPayloadV2 {
  id?: string;
  logoUrl?: string;
  partnerId: string;
  integrationEnabled?: boolean;
  integration?: IntegrationType;
  supportedServices?: ServiceType[];
  name: string;
  active: boolean;
  available: boolean;
  contacts: Partial<Contact>[];
  schedule: Partial<DepartmentSchedule>[];
  location: Partial<PhysicalAddress>;
}

export interface DoctorPayloadV2 {
  id?: string;
  partnerId: string;
  integration?: IntegrationType;
  fullName: string;
  specialization?: string;
  specializations?: string[];
  presentationVideoUrl?: string;
  isPersonalDoctor?: boolean;
  isPopular?: boolean;
  active?: boolean;
  available?: boolean;
}

export interface OfferingPayloadV2 {
  $type: number;
  id?: string;
  partnerId: string;
  integration?: IntegrationType;
  serviceType: string;
  name: string;
  description?: string;
  externalId: string;
  externalCode: string;
  code?: string;
  alternativeCodes?: string[];
  price?: number;
  currency?: string;
  active: boolean;
  available: boolean;
  allowedForGenders?: Gender[];
  minAge?: number;
  maxAge?: number;
  canBeOrderedOnline?: boolean;
  canProduceMedicalData?: boolean;
  appointmentCategory?: AppointmentCategory;
}

// ============================================================================
// SEARCH MODEL INTERFACES - Query/filter types
// ============================================================================

export interface BasePartnerSearchModel {
  searchTerm?: string;
  active?: boolean;
  available?: boolean;
  integrationEnabled?: boolean;
  integration?: IntegrationType;
  supportedServices?: ServiceType[];
  importStatus?: PartnerImportStatus;
  coordinates?: Coordinates;
  radiusInMeters?: number;
  continuationToken?: string;
  pageSize?: number;
  includeDeleted?: boolean;
  sortBy?: string;
  sortDirection?: SortDirection;
}

export interface PartnerSearchModelV2 extends BasePartnerSearchModel {}

export interface PartnerScopedSearchModelV2 extends BasePartnerSearchModel {
  partnerId?: string;
}

export interface LocationSearchModelV2 extends PartnerScopedSearchModelV2 {
  partnerId?: string;
  city?: string;
  address?: string;
}

export interface DoctorSearchModelV2 extends PartnerScopedSearchModelV2 {
  partnerId?: string;
}

export interface OfferingSearchModelV2 extends PartnerScopedSearchModelV2 {
  partnerId?: string;
  mappedOnly?: boolean;
}

// ============================================================================
// FORM CONTROL INTERFACES - Angular Reactive Forms types
// ============================================================================

export interface PartnerControls {
  id?: FormControl<string | null>;
  integrationEnabled?: FormControl<boolean | null>;
  integration?: FormControl<IntegrationType | null>;
  supportedServices: FormControl<ServiceType[]>;
  name: FormControl<string | null>;
  active: FormControl<boolean | null>;
  available: FormControl<boolean | null>;
  contacts: FormArray<FormGroup<ContactControls>>;
}

export interface LocationControls {
  id?: FormControl<string | null>;
  integrationEnabled?: FormControl<boolean | null>;
  integration?: FormControl<IntegrationType | null>;
  supportedServices: FormControl<ServiceType[]>;
  logoUrl: FormControl<string | null>;
  name: FormControl<string | null>;
  active: FormControl<boolean | null>;
  available: FormControl<boolean | null>;
  contacts: FormArray<FormGroup<ContactControls>>;
  partnerId?: FormControl<string | null>;
  logoFileName?: FormControl<string | null>;
  location: FormGroup<{
    city: FormControl<string | null>;
    address: FormControl<string | null>;
    coordinates: FormGroup<{
      latitude: FormControl<number | null>;
      longitude: FormControl<number | null>;
    }>;
  }>;
  schedule: FormArray<FormGroup<DepartmentScheduleControls>>;
}

export interface DoctorControls {
  id?: FormControl<string | null>;
  integration?: FormControl<IntegrationType | null>;
  fullName: FormControl<string | null>;
  specialization?: FormControl<string | null>;
  specializations?: FormArray<FormControl<string | null>>;
  presentationVideoUrl?: FormControl<string | null>;
  isPersonalDoctor?: FormControl<boolean | null>;
  isPopular?: FormControl<boolean | null>;
  partnerId?: FormControl<string | null>;
  active?: FormControl<boolean | null>;
  available?: FormControl<boolean | null>;
}

export interface OfferingControls {
  id?: FormControl<string | null>;
  integration?: FormControl<IntegrationType | null>;
  serviceType?: FormControl<string | null>;
  name: FormControl<string | null>;
  description?: FormControl<string | null>;
  externalId: FormControl<string | null>;
  externalCode?: FormControl<string | null>;
  code?: FormControl<string | null>;
  alternativeCodes?: FormArray<FormControl<string | null>>;
  price?: FormControl<number | null>;
  currency?: FormControl<string | null>;
  partnerId?: FormControl<string | null>;
  allowedForGenders?: FormControl<Gender[] | null>;
  minAge?: FormControl<number | null>;
  maxAge?: FormControl<number | null>;
  canBeOrderedOnline?: FormControl<boolean | null>;
  canProduceMedicalData?: FormControl<boolean | null>;
  appointmentCategory?: FormControl<AppointmentCategory | null>;
  active?: FormControl<boolean | null>;
  available?: FormControl<boolean | null>;
}

export interface ContactControls {
  name: FormControl<string | null>;
  type: FormControl<string | null>;
  contactDetails: FormGroup<{
    phoneNumber: FormControl<string | null>;
    email: FormControl<string | null>;
  }>;
  location: FormGroup<{
    city: FormControl<string | null>;
    address: FormControl<string | null>;
    coordinates: FormControl<Coordinates | null>;
  }>;
}

export interface DepartmentScheduleControls {
  daysOfWeek: FormControl<string[] | null>;
  from: FormControl<string | null>;
  to: FormControl<string | null>;
  formattedHours?: FormControl<string | null>;
}
